<template>
  <div>
    <div class="mt-4 w-100" v-if="!loading">
      <v-row class="d-flex justify-space-between align-center">
        <Button outlined class="advanced-filters-btn ma-5" @click="filtersOpen = !filtersOpen">
          ADVANCED FILTERS
          <span class="mdi mdi-chevron-down"></span>
        </Button>
        <Button outlined class="ma-5" v-if="totalCount > 0" @click="onDownloadClick">
          DOWNLOAD
        </Button>
      </v-row>
    </div>
    <v-expand-transition>
      <div class="ma-5 w-100" v-show="filtersOpen">
        <AuditClaimsFilters
          :value="filters"
          @input="filters = $event"
          :status-options="statusOptions"
          @filters-change="onFiltersChange"
        />
      </div>
    </v-expand-transition>
    <ClaimsTable
      ref="claimsTable"
      :items="allItems"
      :loading="loading"
      :error="error"
      :total-count="totalCount"
      :current-page="currentPage"
      :items-per-page="itemsPerPage"
      :sort-by="sortBy"
      :sort-desc="sortDesc"
      @retry="fetchAuditClaims(filters)"
      @page-change="onPageChange"
      @items-per-page-change="onPageChange"
      @sort-change="onSortChange"
      @approve-click="onApproveClick"
      @reject-click="onRejectClick"
    />
  </div>
</template>

<script>
import ClaimsTable from "@/components/data-table/AuditClaimsTable.vue";
import AuditClaimsFilters from "@/components/filters/AuditClaimsFilters.vue";
import ProgramClaimService from "@/services/ProgramClaimService";
import { debounce } from "@/shared_data/functions";
import Button from "@/components/generic/Button.vue";
import { mapState } from "vuex";

export default {
  name: "AuditClaimsTab",
  components: {
    ClaimsTable,
    AuditClaimsFilters,
    Button,
  },
  data: () => ({
    loading: false,
    error: null,
    allItems: [],
    totalCount: 0,
    currentPage: 1,
    itemsPerPage: 20,
    sortBy: "claimDate",
    sortDesc: false,
    applyDefaultStatus: true,
    statusOptions: ["Pending Audit", "Needs More Info", "Approved", "Denied"],
    filters: {
      status: "Pending",
      claimId: "",
      submitter: "",
      dealerName: "",
      claimsFromDate: null,
      claimsToDate: null,
      salesFromDate: null,
      salesToDate: null,
    },
    filtersOpen: false,
  }),
  computed: {
    ...mapState(["globalClientId"]),
  },
  methods: {
    async fetchAuditClaims(filters = {}, page = 1) {
      try {
        this.loading = true;
        this.error = null;

        let limit = this.itemsPerPage;
        let offset = (page - 1) * this.itemsPerPage;

        if (this.itemsPerPage === -1) {
          limit = 999999; // ensure get all records
          offset = 0;
        }

        // Concat sorting, may need to tweak it after testing
        const sortDirection = this.sortDesc ? "Desc" : "ASC";
        const sortParam = `${this.sortBy} ${sortDirection}`;

        // "Pending" only on first load,
        const computedReviewStatus =
          filters.status !== null && filters.status !== ""
            ? filters.status
            : this.applyDefaultStatus
            ? "Pending"
            : undefined;

        const apiParams = {
          limit: limit,
          offset: offset,
          sort: sortParam,
          claimId: filters.claimId || undefined,
          clientId: this.globalClientId,
          reviewStatus: computedReviewStatus,
          submitterName: filters.submitter || undefined,
          dealerName: filters.dealerName || undefined,
          claimDateStart: filters.claimsFromDate || undefined,
          claimDateEnd: filters.claimsToDate || undefined,
          salesDateStart: filters.salesFromDate || undefined,
          salesDateEnd: filters.salesToDate || undefined,
        };

        const response = await ProgramClaimService.fetchAuditClaimsRecords(apiParams);
        this.allItems = response.rows;
        this.totalCount = response.count;
        this.currentPage = page;
        if (this.applyDefaultStatus) this.applyDefaultStatus = false;
      } catch (err) {
        console.error("Error fetching audit claims:", err);
        this.error = err.message || "Failed to load audit claims.";
      } finally {
        this.loading = false;
      }
    },
    onFiltersChange(newFilters, immediate = false) {
      this.filters = { ...newFilters };
      // Reset to first page when filters change
      this.currentPage = 1;

      if (immediate) {
        this.fetchAuditClaims(newFilters, 1);
      } else {
        this.debouncedFetchClaims(newFilters);
      }
    },

    onPageChange(payload) {
      // Seems like there's a bug with onPageChange and onItemsPerPageChange.
      // if you are on a page other than 1, and items per row is changes, it still calls onPageChange instead of onItemsPerPageChange.
      // Leave for now, will lookg at it later to identify the bug
      if (payload && typeof payload === "object") {
        if (payload.itemsPerPage !== undefined) {
          const itemsPerPage = payload.itemsPerPage;
          this.itemsPerPage = itemsPerPage;
          this.currentPage = 1;
          this.fetchAuditClaims(this.filters, 1);
          return;
        }

        if (payload.page !== undefined) {
          const page = payload.page;
          this.fetchAuditClaims(this.filters, page);
          return;
        }
      }
      if (typeof payload === "number") {
        this.fetchAuditClaims(this.filters, payload);
        return;
      }

      // Unknown payload - no-op
      console.warn("onPageChange received unknown payload:", payload);
    },

    onSortChange(sortBy, sortDesc) {
      this.sortBy = sortBy;
      this.sortDesc = sortDesc;
      this.currentPage = 1;
      this.fetchAuditClaims(this.filters, 1);
    },

    async onApproveClick(payload) {
      const { item, finalStatus } = payload;

      try {
        const comment = (item.auditComment && item.auditComment.trim()) || "";

        await ProgramClaimService.updateAuditClaimsRecord({
          userId: item.submitterId,
          formSubmissionEntryId: item.formSubmissionEntryId,
          formId: item.formId,
          formSubmissionId: item.formSubmissionId,
          reviewStatus: finalStatus,
          reviewerComment: comment,
          clientId: this.globalClientId,
        });

        // Emit success event to table component
        this.$refs.claimsTable.onApproveSuccess(item, finalStatus);
      } catch (err) {
        console.error("Error updating audit claim:", err);

        // Emit error event to table component
        this.$refs.claimsTable.onApproveError(item);
      }
    },

    // TODO: do we still need this
    // eslint-disable-next-line no-unused-vars
    onRejectClick(payload) {
      // const { claimId, item } = payload;
    },

    async onDownloadClick() {
      try {
        this.loading = true;

        const apiParams = {
          limit: 999999,
          offset: 0,
          format: "csv",
          sort: `${this.sortBy} ${this.sortDesc ? "Desc" : "ASC"}`,
          claimId: this.filters.claimId || undefined,
          clientId: this.globalClientId,
          reviewStatus: this.filters.status || undefined,
          submitterName: this.filters.submitter || undefined,
          dealerName: this.filters.dealerName || undefined,
          claimDateStart: this.filters.claimsFromDate || undefined,
          claimDateEnd: this.filters.claimsToDate || undefined,
          salesDateStart: this.filters.salesFromDate || undefined,
          salesDateEnd: this.filters.salesToDate || undefined,
        };

        const { blob: csvData } = await ProgramClaimService.fetchAuditClaimsRecords(
          apiParams,
          true
        );

        this.downloadCSVFile(csvData, "audit-claims.csv");
      } catch (err) {
        console.error("Error downloading CSV:", err);
        this.$store.dispatch("showSnackbar", {
          text: "Failed to download CSV file",
          color: "error",
        });
      } finally {
        this.loading = false;
      }
    },
    downloadCSVFile(csvData, filename) {
      const blob = new Blob([csvData], { type: "text/csv;charset=utf-8;" });

      // So it works on Edge
      if (window.navigator && window.navigator.msSaveOrOpenBlob) {
        window.navigator.msSaveOrOpenBlob(blob, filename);
      } else {
        const url = URL.createObjectURL(blob);
        const link = document.createElement("a");
        link.href = url;
        link.setAttribute("download", filename);
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        //cleanup
        URL.revokeObjectURL(url);
      }

      this.$store.dispatch("showSnackbar", {
        text: "CSV file downloaded successfully",
        color: "success",
      });
    },

    refreshClientId() {
      this.currentPage = 1;
      this.fetchAuditClaims(this.filters, 1);
    },
  },
  created() {
    this.debouncedFetchClaims = debounce((filters) => {
      this.fetchAuditClaims(filters, 1);
    }, 500);
  },
  async mounted() {
    await this.fetchAuditClaims(this.filters, 1);
  },
  watch: {
    globalClientId: function () {
      this.refreshClientId();
    },
  },
};
</script>

<style scoped>
.advanced-filters-btn {
  color: #077daf !important;
  text-transform: none;
  border: none;
}
.advanced-filters-btn span {
  color: #077daf !important;
  font-size: 2em;
}
</style>
